import {
  DecimalPipe,
  getCurrencySymbol,
  NgIf,
  NgFor,
  NgTemplateOutlet,
  NgClass,
  AsyncPipe,
} from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RootState } from '@next/core-lib/app';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { QuickSimulationTableRow } from '../../../models/feed-profit.model';

@Component({
  selector: 'app-quick-simulation-report',
  templateUrl: './quick-simulation-report.component.html',
  styleUrls: ['./quick-simulation-report.component.scss'],
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NgClass,
    MatIcon,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class QuickSimulationReportComponent implements OnInit {
  @Input() rows: QuickSimulationTableRow[];

  customer$: Observable<SelectedCustomer> = this.store.select(UserSelectors.selectCustomer);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  currencySymbol: string;
  comparisonRow: QuickSimulationTableRow;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly decimalPipe: DecimalPipe,
  ) {}

  ngOnInit(): void {
    this.currencySymbol = getCurrencySymbol(this.localeService.getCustomerCurrency(), 'narrow');
  }

  getComparisonRow(): QuickSimulationTableRow {
    if (!this.comparisonRow) {
      this.comparisonRow = this.rows?.find((row) => row?.isComparisonSim);
    }

    return this.comparisonRow;
  }

  getData(value: number, comparisonValue: number, digits = '1.01-2') {
    const diff = this.getDifferenceValue(value, comparisonValue);

    return {
      rowVal: this.getValue(value, digits),
      difference: diff,
      digitsInfo: digits,
      positive: this.isPositive(diff),
    };
  }

  getDifferenceValue(value: number, comparisonValue: number, digitsInfo = '1.01-2') {
    // If the row does not exist or is the comparison row, we do not have to calculate
    // the difference, it will always be null.
    if (value === comparisonValue) {
      return null;
    }

    // Calculate the difference, use 0 if no value is available since we still want a number.
    const differenceVal = value - comparisonValue;
    if (
      differenceVal === null ||
      differenceVal === undefined ||
      isNaN(differenceVal) ||
      !isFinite(differenceVal)
    ) {
      return null;
    }
    return this.decimalPipe.transform(differenceVal, digitsInfo, this.localeService.getLocale());
  }

  getValue(value: number, digitsInfo = '1.01-2') {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return this.localeService.translate('n/a');
    }

    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  isPositive(text: string): number {
    const parsedText = parseFloat(text?.replace(',', '.')) * 100;
    return parsedText > 0 ? 1 : parsedText < 0 ? -1 : 0;
  }
}
